import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services/api';
import { extractErrorMessage } from '../../../utils/errorHandler';
import '../../shared.css';
import './KGWizard.css';
import StepIdentity from './steps/StepIdentity';
import StepFiles from './steps/StepFiles';
import StepConfig from './steps/StepConfig';
import StepVersionFiles from './steps/StepVersionFiles';
import { SelectedFile, FileItem, Workspace } from './steps/StepFiles';

// ── Constants ──────────────────────────────────────────────────────────────
const WS_PAGE = 20;
const FILE_PAGE = 20;
const DRAFT_KEY_KG = 'kgWizardDraft';
const draftKeyVersion = (kgId: string) => `versionWizardDraft_${kgId}`;

// ── Types ──────────────────────────────────────────────────────────────────
type Errors = Partial<Record<'name' | 'description' | 'dataVersion' | 'nodeFiles', string>>;

interface StepDef {
  label: string;
  sub?: string;
}

export interface KGWizardProps {
  mode: 'kg' | 'version';
  kgId?: string;        // required when mode === 'version'
  onClose: () => void;
  onCreated: () => void;
  hasGdsLicense: boolean;
}

// ── Check icon ─────────────────────────────────────────────────────────────
function CheckIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
const KGWizard: React.FC<KGWizardProps> = ({ mode, kgId = '', onClose, onCreated, hasGdsLicense }) => {
  const navigate = useNavigate();
  const isVersion = mode === 'version';

  // Steps: KG = [Identity, Files, Config]; Version = [Files+meta, Config]
  const steps: StepDef[] = isVersion
    ? [
        { label: 'Data files', sub: 'Files & metadata' },
        { label: 'Configuration', sub: 'GDS & review' },
      ]
    : [
        { label: 'Identity', sub: 'Name & tags' },
        { label: 'Data files', sub: 'Node & edge files' },
        { label: 'Configuration', sub: 'GDS & review' },
      ];

  const [step, setStep] = useState(0);
  const maxStep = steps.length - 1;
  const [errors, setErrors] = useState<Errors>({});

  // Form state
  const [name, setName]             = useState('');
  const [nameAvailability, setNameAvailability] = useState<'idle'|'checking'|'available'|'taken'>('idle');
  const [description, setDescription]       = useState('');
  const [dataVersion, setDataVersion] = useState('');
  const [nodeFiles, setNodeFiles]   = useState<SelectedFile[]>([]);
  const [edgeFiles, setEdgeFiles]   = useState<SelectedFile[]>([]);
  const [gdsEnabled, setGdsEnabled] = useState(false);
  const [gdsLocked, setGdsLocked]   = useState(false); // true when KG already has GDS — cannot be disabled
  const [gdsError, setGdsError]     = useState('');

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted]   = useState(false);
  const [createdId, setCreatedId]   = useState('');

  // Workspace + file browser state
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces]  = useState<Workspace[]>([]);
  const [wsLoading, setWsLoading]    = useState(false);
  const [wsError, setWsError]        = useState('');
  const [wsFrom, setWsFrom]          = useState(0);
  const [wsTotal, setWsTotal]        = useState(0);
  const [files, setFiles]            = useState<FileItem[]>([]);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError]     = useState('');
  const [fileFrom, setFileFrom]       = useState(0);
  const [fileTotal, setFileTotal]     = useState(0);
  const [breadcrumb, setBreadcrumb]   = useState<{ id: string; name: string }[]>([]);
  const [currentParentId, setCurrentParentId] = useState('');
  const [selectingAll, setSelectingAll] = useState(false);

  // ── Draft persistence ──────────────────────────────────────────────────
  const draftKey = isVersion ? draftKeyVersion(kgId) : DRAFT_KEY_KG;

  // ── Restore draft + set GDS default from existing versions ────────────────
  // GDS default from existing versions always takes priority over stale draft.
  useEffect(() => {
    (async () => {
      // 1. Restore non-GDS draft fields
      try {
        const saved = sessionStorage.getItem(draftKey);
        if (saved) {
          const d = JSON.parse(saved);
          if (!isVersion && d.name) setName(d.name);
          if (d.description) setDescription(d.description);
          if (d.dataVersion) setDataVersion(d.dataVersion);
          if (d.nodeFiles) setNodeFiles(d.nodeFiles);
          if (d.edgeFiles) setEdgeFiles(d.edgeFiles);
          if (typeof d.step === 'number') setStep(d.step);
        }
      } catch { /* ignore */ }

      // 2. If creating a version, check existing versions for GDS — this wins
      if (isVersion && kgId) {
        try {
          const res: any = await apiService.listVersions(kgId);
          const items: any[] = Array.isArray(res) ? res : (res?.data ?? []);
          const anyGds = items.some((item: any) => {
            const a = item.attributes ?? item;
            return a.gds_enabled === true;
          });
          if (anyGds) {
            setGdsEnabled(true);
            setGdsLocked(true);
          }
        } catch { /* non-critical, leave default */ }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (submitted) return;
    const timer = setTimeout(() => {
      try {
        sessionStorage.setItem(draftKey, JSON.stringify({
          step, name, description, dataVersion, nodeFiles, edgeFiles, gdsEnabled,
        }));
      } catch { /* ignore */ }
    }, 500);
    return () => clearTimeout(timer);
  }, [step, name, description, dataVersion, nodeFiles, edgeFiles, gdsEnabled, submitted, draftKey]);

  const clearDraft = () => {
    try { sessionStorage.removeItem(draftKey); } catch { /* ignore */ }
  };

  // ── Keyboard close + body scroll lock ────────────────────────────────
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', h);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // ── Name availability check ───────────────────────────────────────────
  const nameCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleNameChange = useCallback((v: string) => {
    setName(v);
    setErrors(prev => ({ ...prev, name: '' }));
    setNameAvailability('idle');
    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);
    if (v.trim().length < 2) return;
    nameCheckTimer.current = setTimeout(async () => {
      setNameAvailability('checking');
      try {
        const res: any = await apiService.listKGs();
        const existing: string[] = (res.data || []).map((k: any) =>
          (k.attributes?.kg_name || k.kg_name || '').toLowerCase()
        );
        setNameAvailability(existing.includes(v.trim().toLowerCase()) ? 'taken' : 'available');
      } catch {
        setNameAvailability('idle');
      }
    }, 600);
  }, []);

  // ── Workspace loading ─────────────────────────────────────────────────
  const loadWorkspaces = useCallback(async (query: string, from: number) => {
    setWsLoading(true);
    setWsError('');
    try {
      const response: any = await apiService.searchWorkspaces('{user_id}', from, WS_PAGE, query);
      if (response.hits) {
        const list = response.hits.hits.map((hit: any) => ({
          id: hit._id,
          name: hit._source.name || hit._source.workspace_name || 'Unnamed Workspace',
          created_date: hit._source.created_date,
          description: hit._source.description || '',
          owner: hit._source.owner || hit._source.created_by || '',
        }));
        setWorkspaces(list);
        setWsTotal(response.hits.total?.value ?? response.hits.total ?? 0);
        setWsFrom(from);
      }
    } catch (err: any) {
      setWsError(extractErrorMessage(err, 'Failed to load workspaces'));
    } finally {
      setWsLoading(false);
    }
  }, []);

  // ── File loading ──────────────────────────────────────────────────────
  const loadFiles = useCallback(async (wsId: string, parentId: string, from: number) => {
    setFileLoading(true);
    setFileError('');
    try {
      const response: any = await apiService.listWorkspaceFiles(
        wsId, parentId, '{document_index}', '{workspace_index}', from, FILE_PAGE
      );
      if (response.hits) {
        const list: FileItem[] = response.hits.hits.map((hit: any) => ({
          id: hit._id,
          name: hit._source.entity_name || hit._source.name || 'Unnamed',
          type: hit._source.entity_type || 'file',
          size: hit._source.size,
          s3_path: hit._source.s3_key || hit._source.s3_path,
          parent_id: hit._source.parent_id || parentId,
        }));
        setFiles(list);
        setFileTotal(response.hits.total?.value ?? response.hits.total ?? 0);
        setFileFrom(from);
      }
    } catch (err: any) {
      setFileError(extractErrorMessage(err, 'Failed to load files'));
    } finally {
      setFileLoading(false);
    }
  }, []);

  const handleWorkspaceSelect = useCallback((ws: Workspace) => {
    setSelectedWorkspace(ws);
    setBreadcrumb([{ id: ws.id, name: ws.name }]);
    setCurrentParentId(ws.id);
    setFiles([]);
    loadFiles(ws.id, ws.id, 0);
  }, [loadFiles]);

  const handleFolderClick = useCallback((folder: FileItem) => {
    if (!selectedWorkspace) return;
    setBreadcrumb(prev => [...prev, { id: folder.id, name: folder.name }]);
    setCurrentParentId(folder.id);
    setFiles([]);
    loadFiles(selectedWorkspace.id, folder.id, 0);
  }, [selectedWorkspace, loadFiles]);

  const handleBreadcrumbClick = useCallback((idx: number) => {
    if (!selectedWorkspace) return;
    setBreadcrumb(prev => {
      const next = prev.slice(0, idx + 1);
      const targetId = next[next.length - 1].id;
      setCurrentParentId(targetId);
      setFiles([]);
      loadFiles(selectedWorkspace.id, targetId, 0);
      return next;
    });
  }, [selectedWorkspace, loadFiles]);

  const handleSelectAll = useCallback(async (onLoaded: (files: FileItem[]) => void) => {
    if (!selectedWorkspace) return;
    setSelectingAll(true);
    setFileError('');
    try {
      const totalPages = Math.ceil(fileTotal / FILE_PAGE);
      const all: FileItem[] = [];
      for (let i = 0; i < totalPages; i++) {
        const response: any = await apiService.listWorkspaceFiles(
          selectedWorkspace.id, currentParentId, '{document_index}', '{workspace_index}', i * FILE_PAGE, FILE_PAGE
        );
        if (response.hits) {
          response.hits.hits.forEach((hit: any) => {
            all.push({
              id: hit._id,
              name: hit._source.entity_name || hit._source.name || 'Unnamed',
              type: hit._source.entity_type || 'file',
              size: hit._source.size,
              s3_path: hit._source.s3_key || hit._source.s3_path,
              parent_id: hit._source.parent_id || currentParentId,
            });
          });
        }
      }
      onLoaded(all);
    } catch (err: any) {
      setFileError(extractErrorMessage(err, 'Failed to select all files'));
    } finally {
      setSelectingAll(false);
    }
  }, [selectedWorkspace, currentParentId, fileTotal]);

  // ── File assignment ───────────────────────────────────────────────────
  const handleAssignFiles = useCallback((incoming: SelectedFile[], bucket: 'node' | 'edge') => {
    const setter = bucket === 'node' ? setNodeFiles : setEdgeFiles;
    setter(prev => {
      const keys = new Set(prev.map(f => f.s3_key));
      return [...prev, ...incoming.filter(f => !keys.has(f.s3_key))];
    });
    if (bucket === 'node') setErrors(prev => ({ ...prev, nodeFiles: '' }));
  }, []);

  // ── Validation ────────────────────────────────────────────────────────
  const validateStep = useCallback((targetStep: number): boolean => {
    const errs: Errors = {};
    if (!isVersion && targetStep > 0) {
      if (!name.trim()) errs.name = 'Name is required';
      if (nameAvailability === 'taken') errs.name = 'Name already in use';
      if (!description.trim()) errs.description = 'Description is required';
      if (!dataVersion.trim()) errs.dataVersion = 'Data version is required';
    }
    if (isVersion && targetStep > 0) {
      if (!description.trim()) errs.description = 'Description is required';
      if (!dataVersion.trim()) errs.dataVersion = 'Data version is required';
      if (nodeFiles.length === 0) errs.nodeFiles = 'At least one node file is required';
    }
    const filesStepIdx = isVersion ? 0 : 1;
    if (targetStep > filesStepIdx && !isVersion) {
      if (nodeFiles.length === 0) errs.nodeFiles = 'At least one node file is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [isVersion, name, nameAvailability, description, dataVersion, nodeFiles]);

  const handleNext = () => {
    if (!validateStep(step + 1)) return;
    setStep(s => Math.min(s + 1, maxStep));
  };

  const handleBack = () => setStep(s => Math.max(s - 1, 0));

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const errs: Errors = {};
    if (!isVersion && !name.trim()) errs.name = 'Name is required';
    if (!description.trim()) errs.description = 'Description is required';
    if (!dataVersion.trim()) errs.dataVersion = 'Data version is required';
    if (nodeFiles.length === 0) errs.nodeFiles = 'At least one node file is required';
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      if (!isVersion && (errs.name || errs.description || errs.dataVersion)) { setStep(0); return; }
      if (errs.nodeFiles) { setStep(isVersion ? 0 : 1); return; }
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setGdsError('');

    try {
      if (isVersion) {
        const res: any = await apiService.createVersion(kgId, {
          kg_description: description.trim(),
          data_version: dataVersion.trim(),
          data_files: {
            nodes: nodeFiles.map(f => f.s3_key),
            edges: edgeFiles.map(f => f.s3_key),
          },
          gds_enabled: gdsEnabled,
        });
        setCreatedId(res?.data?.id || kgId);
      } else {
        const res: any = await apiService.createKG({
          kg_name: name.trim(),
          kg_description: description.trim(),
          data_version: dataVersion.trim(),
          data_files: {
            nodes: nodeFiles.map(f => f.s3_key),
            edges: edgeFiles.map(f => f.s3_key),
          },
          gds_enabled: gdsEnabled,
        });
        setCreatedId(res?.data?.id || name.trim());
      }
      clearDraft();
      setSubmitted(true);
      onCreated();
    } catch (err: any) {
      const msg = extractErrorMessage(err, `Failed to create ${isVersion ? 'version' : 'knowledge graph'}`);
      if (gdsEnabled) {
        setGdsEnabled(false);
        setGdsError('GDS access is unavailable for your account. Please contact your administrator.');
      } else {
        setSubmitError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    clearDraft();
    onClose();
  };

  // ── Step state labels ─────────────────────────────────────────────────
  const getStepState = (idx: number): 'active' | 'done' | 'pending' | 'error' => {
    if (idx === step) return 'active';
    if (idx < step) {
      const hasErr = (
        (!isVersion && idx === 0 && (errors.name || errors.description || errors.dataVersion)) ||
        (idx === (isVersion ? 0 : 1) && errors.nodeFiles)
      );
      return hasErr ? 'error' : 'done';
    }
    return 'pending';
  };

  // ── Render step content ───────────────────────────────────────────────
  const filesStepIdx = isVersion ? 0 : 1;
  const configStepIdx = isVersion ? 1 : 2;

  const renderStep = () => {
    if (!isVersion && step === 0) {
      return (
        <StepIdentity
          name={name}
          onNameChange={handleNameChange}
          nameError={errors.name || ''}
          nameAvailability={nameAvailability}
          description={description}
          onDescriptionChange={v => { setDescription(v); setErrors(prev => ({ ...prev, description: '' })); }}
          descriptionError={errors.description || ''}
          dataVersion={dataVersion}
          onDataVersionChange={v => { setDataVersion(v); setErrors(prev => ({ ...prev, dataVersion: '' })); }}
          dataVersionError={errors.dataVersion || ''}
        />
      );
    }

    if (step === filesStepIdx) {
      const filesProps = {
        nodeFiles, edgeFiles,
        onRemoveNodeFile: (i: number) => setNodeFiles(prev => prev.filter((_, idx) => idx !== i)),
        onRemoveEdgeFile: (i: number) => setEdgeFiles(prev => prev.filter((_, idx) => idx !== i)),
        onClearNodeFiles: () => setNodeFiles([]),
        onClearEdgeFiles: () => setEdgeFiles([]),
        nodeFilesError: errors.nodeFiles || '',
        onAssignFiles: handleAssignFiles,
        selectedWorkspace, workspaces, wsLoading, wsError, wsTotal, wsFrom, WS_PAGE,
        onWorkspaceSelect: handleWorkspaceSelect,
        onLoadWorkspaces: loadWorkspaces,
        files, fileLoading, fileError, fileTotal, fileFrom, FILE_PAGE,
        breadcrumb, onFolderClick: handleFolderClick, onBreadcrumbClick: handleBreadcrumbClick,
        onLoadFiles: loadFiles, currentParentId,
        onSelectAll: handleSelectAll, selectingAll,
      };

      if (isVersion) {
        return (
          <StepVersionFiles
            {...filesProps}
            description={description}
            onDescriptionChange={v => { setDescription(v); setErrors(prev => ({ ...prev, description: '' })); }}
            descriptionError={errors.description || ''}
            dataVersion={dataVersion}
            onDataVersionChange={v => { setDataVersion(v); setErrors(prev => ({ ...prev, dataVersion: '' })); }}
            dataVersionError={errors.dataVersion || ''}
            gdsEnabled={gdsEnabled}
            gdsLocked={gdsLocked}
            hasGdsLicense={hasGdsLicense}
            onGdsToggle={() => { if (!gdsLocked && hasGdsLicense) { setGdsEnabled(v => !v); setGdsError(''); } }}
            gdsError={gdsError}
          />
        );
      }

      return <StepFiles {...filesProps} />;
    }

    if (step === configStepIdx) {
      return (
        <StepConfig
          mode={mode}
          name={name}
          kgId={kgId}
          description={description}
          dataVersion={dataVersion}
          nodeFiles={nodeFiles}
          edgeFiles={edgeFiles}
          gdsEnabled={gdsEnabled}
          gdsLocked={gdsLocked}
          hasGdsLicense={hasGdsLicense}
          onGdsToggle={() => { if (!gdsLocked && hasGdsLicense) { setGdsEnabled(v => !v); setGdsError(''); } }}
          gdsError={gdsError}
        />
      );
    }

    return null;
  };

  const isLastStep = step === maxStep;
  const title = isVersion ? 'Create new version' : 'Create knowledge graph';

  return (
    <div className="wiz-overlay" onClick={handleCancel}>
      <div className="wiz-dialog" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="wiz-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <span className="wiz-header-title">{title}</span>
            {isVersion && kgId && (
              <span className="wiz-header-meta"> · {kgId}</span>
            )}
          </div>
          <button className="wiz-close-btn" onClick={handleCancel} title="Close">×</button>
        </div>

        {/* Body = rail + step */}
        <div className="wiz-body">

          {/* Step rail */}
          <div className="wiz-rail">
            {steps.map((s, idx) => {
              const state = getStepState(idx);
              return (
                <button
                  key={idx}
                  className={`wiz-step-btn wiz-step-btn--${state}`}
                  onClick={() => { if (state === 'done') setStep(idx); }}
                  disabled={state === 'pending' || state === 'active'}
                >
                  <div className="wiz-step-indicator">
                    {state === 'done' ? <CheckIcon /> : state === 'error' ? '!' : idx + 1}
                  </div>
                  <div className="wiz-step-info">
                    <span className="wiz-step-label">{s.label}</span>
                    {s.sub && <span className="wiz-step-sub">{s.sub}</span>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Step content */}
          <div className="wiz-step" style={step === filesStepIdx ? { overflow: 'hidden', display: 'flex', flexDirection: 'column' } : {}}>
            {submitted ? (
              <div className="wiz-success">
                <div className="wiz-success-icon">✓</div>
                <div className="wiz-success-title">
                  {isVersion ? 'Version created successfully' : 'Knowledge graph created'}
                </div>
                <div className="wiz-success-sub">
                  {isVersion
                    ? `A new version has been created for ${kgId}.`
                    : `"${name}" has been created and is ready to use.`}
                </div>
                {!isVersion && (
                  <button
                    className="wiz-success-link"
                    onClick={() => { onClose(); navigate(`/kg/${createdId}`); }}
                  >
                    View knowledge graph →
                  </button>
                )}
                <button
                  className="wiz-cancel-btn"
                  style={{ marginTop: 4 }}
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            ) : (
              renderStep()
            )}
          </div>
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="wiz-footer">
            <div className="wiz-footer-left">
              <button className="wiz-cancel-btn" onClick={handleCancel}>Cancel</button>
            </div>

            {submitting ? (
              <div className="wiz-progress-footer">
                <span className="wiz-progress-label">Creating…</span>
                <div className="wiz-progress-bar-wrap">
                  <div className="wiz-progress-bar-fill" style={{ width: '60%' }} />
                </div>
              </div>
            ) : (
              <div className="wiz-footer-right">
                {submitError && (
                  <span style={{ fontSize: 12, color: 'var(--destructive)', fontFamily: 'var(--font-primary)', maxWidth: 300 }}>
                    {submitError}
                  </span>
                )}
                {step > 0 && (
                  <button className="wiz-back-btn" onClick={handleBack}>‹ Back</button>
                )}
                {isLastStep ? (
                  <button className="wiz-next-btn" onClick={handleSubmit} disabled={submitting}>
                    {isVersion ? 'Create version' : 'Create knowledge graph'}
                  </button>
                ) : (
                  <button className="wiz-next-btn" onClick={handleNext}>
                    Next ›
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default KGWizard;
